import { ActionIcon, Button, Group, Stack, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    CityQuestFinaleChoiceText,
    CityQuestHintText,
    CityQuestPrologueScreen,
} from '@routellers/types/dist/city-quest/city-quest-parts';
import { CityQuestLocaleAudioPart } from '@routellers/types/dist/city-quest/generate-city-quest-audio.dto';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';
import { Section } from '../../components/editor';

export type ArcLocaleDraft = {
    prologue: CityQuestPrologueScreen[];
    prologueAudioUrl: string;
    finaleChoicePrompt: string;
    finaleChoices: CityQuestFinaleChoiceText[];
    finalPuzzlePrompt: string;
    finalPuzzleAcceptedAnswers: string;
    finalPuzzleHints: CityQuestHintText[];
    finalPuzzleSolutionExplanation: string;
    finaleAudioUrl: string;
};

export const emptyArcLocale = (): ArcLocaleDraft => ({
    prologue: [],
    prologueAudioUrl: '',
    finaleChoicePrompt: '',
    finaleChoices: [],
    finalPuzzlePrompt: '',
    finalPuzzleAcceptedAnswers: '',
    finalPuzzleHints: [],
    finalPuzzleSolutionExplanation: '',
    finaleAudioUrl: '',
});

export const csv = (list: string[] | null | undefined) => (list || []).join(', ');

export const fromCsv = (value: string) =>
    value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

export const prologueFilled = (draft?: ArcLocaleDraft | null) =>
    !!draft?.prologue.some((screen) => screen.text.trim());

export const finaleFilled = (draft?: ArcLocaleDraft | null) => {
    if (!draft) return false;

    return (
        !!draft.finaleChoicePrompt.trim() ||
        draft.finaleChoices.some((choice) => choice.label.trim() || choice.ending.trim()) ||
        !!draft.finalPuzzlePrompt.trim()
    );
};

export const arcLocaleFilled = (draft?: ArcLocaleDraft | null) =>
    prologueFilled(draft) || finaleFilled(draft);

type ArcAudioFieldProps = {
    questId: number;
    language: Languages;
    part: CityQuestLocaleAudioPart;
    /** Текст с экрана — чтобы озвучка не отставала от несохранённых правок. */
    sourceText: string;
    value: string;
    onChange: (url: string) => void;
};

/** URL озвучки пролога/финала с кнопкой генерации TTS. */
const ArcAudioField = ({
    questId,
    language,
    part,
    sourceText,
    value,
    onChange,
}: ArcAudioFieldProps) => {
    const { t } = useTranslation();
    const [generating, setGenerating] = useState(false);

    const generate = async () => {
        setGenerating(true);

        try {
            const { data } = await api.cityQuestsApi.generateLocaleAudio({
                id: questId,
                data: { language, part, text: sourceText.trim() || undefined },
            });

            if (data?.url) onChange(data.url);
            notifications.show({
                color: 'green',
                message: t('editors.quest.locales.audioGenerated'),
            });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Group gap='xs' align='flex-end' wrap='nowrap'>
            <TextInput
                label={t(`editors.quest.locales.${part}.audioUrl`)}
                value={value}
                onChange={(e) => onChange(e.currentTarget.value)}
                style={{ flex: 1 }}
            />
            <Button size='xs' variant='light' loading={generating} onClick={generate} mb={2}>
                {t('editors.quest.locales.generateTts')}
            </Button>
        </Group>
    );
};

type PrologueProps = {
    questId: number;
    language: Languages;
    draft: ArcLocaleDraft;
    onChange: (patch: Partial<ArcLocaleDraft>) => void;
};

export const PrologueArcPanel = ({ questId, language, draft, onChange }: PrologueProps) => {
    const { t } = useTranslation();

    return (
        <Section
            boxed
            title={t('editors.quest.locales.prologue.title')}
            description={t('editors.quest.locales.prologue.description')}
        >
            <Stack gap='xs'>
                {draft.prologue.map((screen, index) => (
                    <Group key={`${screen.key}-${index}`} align='flex-start' gap='xs' wrap='nowrap'>
                        <TextInput
                            label={t('editors.quest.locales.prologue.key')}
                            size='xs'
                            value={screen.key}
                            onChange={(e) => {
                                const next = [...draft.prologue];

                                next[index] = { ...screen, key: e.currentTarget.value };
                                onChange({ prologue: next });
                            }}
                            style={{ width: 150 }}
                        />
                        <Textarea
                            label={t('editors.quest.locales.prologue.text')}
                            minRows={3}
                            autosize
                            value={screen.text}
                            onChange={(e) => {
                                const next = [...draft.prologue];

                                next[index] = { ...screen, text: e.currentTarget.value };
                                onChange({ prologue: next });
                            }}
                            style={{ flex: 1 }}
                        />
                        <ActionIcon
                            color='red'
                            variant='subtle'
                            mt={26}
                            onClick={() =>
                                onChange({
                                    prologue: draft.prologue.filter((_, i) => i !== index),
                                })
                            }
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Group>
                ))}
                <Button
                    size='xs'
                    variant='light'
                    leftSection={<IconPlus size={14} />}
                    onClick={() =>
                        onChange({
                            prologue: [
                                ...draft.prologue,
                                {
                                    key: `screen_${draft.prologue.length + 1}`,
                                    text: '',
                                },
                            ],
                        })
                    }
                >
                    {t('editors.quest.locales.prologue.addScreen')}
                </Button>
                <ArcAudioField
                    questId={questId}
                    language={language}
                    part='prologue'
                    sourceText={draft.prologue.map((screen) => screen.text).join('\n\n')}
                    value={draft.prologueAudioUrl}
                    onChange={(url) => onChange({ prologueAudioUrl: url })}
                />
            </Stack>
        </Section>
    );
};

type FinaleProps = {
    questId: number;
    language: Languages;
    draft: ArcLocaleDraft;
    onChange: (patch: Partial<ArcLocaleDraft>) => void;
};

export const FinaleArcPanel = ({ questId, language, draft, onChange }: FinaleProps) => {
    const { t } = useTranslation();

    return (
        <Section
            boxed
            title={t('editors.quest.locales.finale.title')}
            description={t('editors.quest.locales.finale.description')}
        >
            <Textarea
                label={t('editors.quest.locales.finale.choicePrompt')}
                minRows={2}
                autosize
                value={draft.finaleChoicePrompt}
                onChange={(e) => onChange({ finaleChoicePrompt: e.currentTarget.value })}
            />
            <Stack gap='xs'>
                {draft.finaleChoices.map((choice, index) => (
                    <Stack key={choice.id || index} gap={4}>
                        <Group gap='xs' wrap='nowrap'>
                            <TextInput
                                label={t('editors.quest.locales.finale.id')}
                                size='xs'
                                value={choice.id}
                                onChange={(e) => {
                                    const next = [...draft.finaleChoices];

                                    next[index] = { ...choice, id: e.currentTarget.value };
                                    onChange({ finaleChoices: next });
                                }}
                                style={{ width: 150 }}
                            />
                            <TextInput
                                label={t('editors.quest.locales.finale.label')}
                                size='xs'
                                value={choice.label}
                                onChange={(e) => {
                                    const next = [...draft.finaleChoices];

                                    next[index] = { ...choice, label: e.currentTarget.value };
                                    onChange({ finaleChoices: next });
                                }}
                                style={{ flex: 1 }}
                            />
                            <ActionIcon
                                color='red'
                                variant='subtle'
                                mt={20}
                                onClick={() =>
                                    onChange({
                                        finaleChoices: draft.finaleChoices.filter(
                                            (_, i) => i !== index,
                                        ),
                                    })
                                }
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                        <Textarea
                            label={t('editors.quest.locales.finale.ending')}
                            minRows={3}
                            autosize
                            value={choice.ending}
                            onChange={(e) => {
                                const next = [...draft.finaleChoices];

                                next[index] = { ...choice, ending: e.currentTarget.value };
                                onChange({ finaleChoices: next });
                            }}
                        />
                    </Stack>
                ))}
                <Button
                    size='xs'
                    variant='light'
                    leftSection={<IconPlus size={14} />}
                    onClick={() =>
                        onChange({
                            finaleChoices: [
                                ...draft.finaleChoices,
                                {
                                    id: `ending_${draft.finaleChoices.length + 1}`,
                                    label: '',
                                    ending: '',
                                },
                            ],
                        })
                    }
                >
                    {t('editors.quest.locales.finale.addEnding')}
                </Button>
            </Stack>
            <Textarea
                label={t('editors.quest.locales.finale.finalPuzzlePrompt')}
                minRows={2}
                autosize
                value={draft.finalPuzzlePrompt}
                onChange={(e) => onChange({ finalPuzzlePrompt: e.currentTarget.value })}
            />
            <TextInput
                label={t('editors.quest.locales.finale.finalPuzzleAnswers')}
                value={draft.finalPuzzleAcceptedAnswers}
                onChange={(e) => onChange({ finalPuzzleAcceptedAnswers: e.currentTarget.value })}
            />
            <Stack gap='xs'>
                {draft.finalPuzzleHints.map((hint, index) => (
                    <Group key={index} align='flex-start' gap='xs' wrap='nowrap'>
                        <Textarea
                            label={t('editors.quest.locales.finale.finalPuzzleHint', {
                                n: hint.level,
                            })}
                            minRows={2}
                            autosize
                            value={hint.text}
                            onChange={(e) => {
                                const next = [...draft.finalPuzzleHints];

                                next[index] = { ...hint, text: e.currentTarget.value };
                                onChange({ finalPuzzleHints: next });
                            }}
                            style={{ flex: 1 }}
                        />
                        <ActionIcon
                            color='red'
                            variant='subtle'
                            mt={26}
                            onClick={() =>
                                onChange({
                                    finalPuzzleHints: draft.finalPuzzleHints
                                        .filter((_, i) => i !== index)
                                        .map((row, i) => ({ ...row, level: i + 1 })),
                                })
                            }
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Group>
                ))}
                <Button
                    size='xs'
                    variant='light'
                    leftSection={<IconPlus size={14} />}
                    onClick={() =>
                        onChange({
                            finalPuzzleHints: [
                                ...draft.finalPuzzleHints,
                                { level: draft.finalPuzzleHints.length + 1, text: '' },
                            ],
                        })
                    }
                >
                    {t('editors.quest.locales.finale.addFinalPuzzleHint')}
                </Button>
            </Stack>
            <Textarea
                label={t('editors.quest.locales.finale.finalPuzzleExplanation')}
                minRows={2}
                autosize
                value={draft.finalPuzzleSolutionExplanation}
                onChange={(e) =>
                    onChange({ finalPuzzleSolutionExplanation: e.currentTarget.value })
                }
            />
            <ArcAudioField
                questId={questId}
                language={language}
                part='finale'
                sourceText={draft.finaleChoicePrompt}
                value={draft.finaleAudioUrl}
                onChange={(url) => onChange({ finaleAudioUrl: url })}
            />
        </Section>
    );
};
